import { FC } from 'react';
import { useHistory, useParams } from 'react-router';
import { Flex, Image, Stack } from '@chakra-ui/react';

import {
  FeatureFilterField,
  FeatureName,
  TeamMembersRequestStatus,
  UserFilterValues,
} from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import useCompanyContext from 'common/hooks/useCompanyContext';
import paymentCardsLocales from 'common/paymentCards/locales';
import { getCardRequestAssetSrc } from 'common/paymentCards/utils';
import ProgressBar from 'components/_core/ProgressBar';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import { getCardRequestPath } from 'features/PaymentCards/routes';
import { RequestsFilterState } from 'features/Team/pages/Requests/types';
import * as teamRoutes from 'features/Team/routes';

import useCanCompanyUserAccessFeature from '../../../../../../common/hooks/useCanCompanyUserAccessFeature';
import {
  isVirtualBudgetCardOrderRequest,
  OngoingPaymentCardRequest,
} from '../../../../hooks/useOngoingPaymentCardsRequests';
import PaymentCardLimitsSkeleton from '../PaymentCardLimitsSkeleton';
import locales from './locales';
import { requestTypeToKey } from './utils';

interface OngoingPaymentCardRequestRowParams {
  creditCardId: OngoingPaymentCardRequest['teamMembersRequestId'];
}

interface OngoingPaymentCardRequestRowProps {
  ongoingPaymentCardRequest: OngoingPaymentCardRequest;
}

const OngoingPaymentCardRequestRow: FC<OngoingPaymentCardRequestRowProps> = ({
  ongoingPaymentCardRequest,
}) => {
  const history = useHistory();
  const { companyUserId } = useCompanyContext();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  const { creditCardId: selectedRequest } =
    useParams<OngoingPaymentCardRequestRowParams>();

  const { requester, status, teamMembersRequestId, type } =
    ongoingPaymentCardRequest;

  const reviewRequestPath = teamRoutes.getRequestPath(
    teamMembersRequestId,
    RequestsFilterState.Pending,
  );

  const ownCardRequestPath = getCardRequestPath(teamMembersRequestId);

  const isCurrentUserCard =
    canAccessFeature({
      name: FeatureName.RequestPaymentcards,
      where: {
        or: [
          { field: FeatureFilterField.User, values: [UserFilterValues.Self] },
        ],
      },
    }) || requester?.companyUserId === companyUserId;
  const isPending = status === TeamMembersRequestStatus.Pending;
  const paymentCardTypeWording = paymentCardsLocales[requestTypeToKey(type)];

  const isSelected = teamMembersRequestId === selectedRequest;

  const { limitPaymentAll = undefined } = isVirtualBudgetCardOrderRequest(
    ongoingPaymentCardRequest,
  )
    ? {
        limitPaymentAll:
          ongoingPaymentCardRequest.reviewerPayload?.limitPaymentAll ??
          ongoingPaymentCardRequest.requesterPayload.limitPaymentAll,
      }
    : {};

  return (
    <SunshineCard
      alignItems="center"
      border="1.5px solid"
      borderColor={isSelected ? 'black' : 'transparent'}
      {...(isPending && {
        onClick: () =>
          isCurrentUserCard
            ? history.push(ownCardRequestPath)
            : history.push(reviewRequestPath),
      })}
      isActive={isSelected}
      // When a request is approved, the card's creation is in progress and we want to disable the row and the user to understand
      // that he can't click on the card (no action is possible on the card during its creation).
      isDisabled={status === TeamMembersRequestStatus.Approved}
      padding="space-16"
    >
      <SunshineCard.Slot name="aside">
        <Flex alignItems="center" gap="space-16" width="40%">
          <Image
            alt=""
            boxSize="40px"
            objectFit="contain"
            src={getCardRequestAssetSrc(ongoingPaymentCardRequest)}
          />

          <Stack spacing="space-0">
            {isCurrentUserCard ? (
              <Text noOfLines={1} variant="secondary">
                {paymentCardTypeWording}
              </Text>
            ) : (
              <>
                {requester ? (
                  <Text fontWeight="weight-500" variant="primary">
                    {requester.firstName}&nbsp;{requester.lastName}
                  </Text>
                ) : null}

                <Text size="sm">{paymentCardTypeWording}</Text>
              </>
            )}
          </Stack>
        </Flex>
      </SunshineCard.Slot>

      {limitPaymentAll ? (
        <SunshineCard.Content>
          <Stack
            alignItems="flex-start"
            flex={1}
            flexDirection="column"
            justifyContent="center"
            spacing="space-8"
          >
            <PaymentCardLimitsSkeleton isLoaded width="auto">
              <Text
                color="grey.400"
                fontWeight="weight-500"
                size="xs"
                variant="primary"
              >
                {locales.formatString(locales.limitDescription, {
                  amount: formatAmount({
                    forcePlusSign: false,
                    value: 0,
                  }),
                  maxAmount: formatAmount({
                    forcePlusSign: false,
                    value: limitPaymentAll,
                  }),
                })}
              </Text>
            </PaymentCardLimitsSkeleton>

            <PaymentCardLimitsSkeleton isLoaded width="175px">
              <ProgressBar
                marginBottom="space-6"
                marginTop="-space-6"
                ratio={0}
              />

              <Text color="grey.400" marginTop="-space-2" size="xs">
                {locales.paymentLimit}
              </Text>
            </PaymentCardLimitsSkeleton>
          </Stack>
        </SunshineCard.Content>
      ) : null}

      <SunshineCard.Slot name="action">
        <SunshineTag colorScheme="blue" size="sm">
          {locales.badgeLabel[status]}
        </SunshineTag>
      </SunshineCard.Slot>
    </SunshineCard>
  );
};

export default OngoingPaymentCardRequestRow;
